import morphdom from 'morphdom';
import { fromEvent, noop, Observable, Subject } from 'rxjs';
import { debounceTime, share, tap } from 'rxjs/operators';
import { eventHasMetaKeyForPlatform } from '../../helpers/platform/modifierKeyHelpers';
import {
  deserializeSelectionBookmarksInElement,
  updateSelectionBookmarksInElement,
} from '../../helpers/selectionBookmarks';
import { UndoManager } from '../../undoManager';
import { createConnectingService } from '../base/connectingService';
import { createService, Service } from '../base/service';
import { NULL_UNDO_SERVICE, UndoService } from '../text/undoService';
import { ElementService } from './elementService';

export function createElementUndoService(
  element$: Observable<Element | null>,
  options: ElementUndoServiceOptions,
  ...snapshotTriggerServices: Service[]
): UndoService {
  return createConnectingService(
    element$,
    () => NULL_UNDO_SERVICE,
    element => new ElementUndoService(element, options, ...snapshotTriggerServices),
  );
}

interface ElementUndoServiceOptions {
  readonly elementToRemoveFromSnapshotSelectors: Set<string>;
  readonly classToRemoveFromSnapshotSelectors: Set<string>;
}

export class ElementUndoService extends ElementService<Element> implements UndoService {
  constructor(element: Element, options: ElementUndoServiceOptions, ...snapshotTriggerServices: Service[]) {
    let didRestoreSnapshot = false;
    let pauseTakingSnapshots = false;
    let snapshotAtPause = element.innerHTML;
    const snapshotTrigger$ = new Subject<void>();

    const getCleanSnapshot = () => {
      const { elementToRemoveFromSnapshotSelectors, classToRemoveFromSnapshotSelectors } = options;
      if (elementToRemoveFromSnapshotSelectors.size + classToRemoveFromSnapshotSelectors.size === 0) {
        return element.innerHTML;
      }
      const document = element.ownerDocument;
      if (!document) {
        return element.innerHTML;
      }
      const container = document.createElement('template');
      container.innerHTML = element.innerHTML;
      const { content } = container;
      elementToRemoveFromSnapshotSelectors.forEach(selector => {
        content.querySelectorAll(selector).forEach(el => el.remove());
      });
      classToRemoveFromSnapshotSelectors.forEach(className => {
        content.querySelectorAll(`.${className}`).forEach(el => el.classList.remove(className));
      });
      return container.innerHTML;
    };

    const undoManager = new UndoManager<string>({
      takeSnapshot: () => {
        if (pauseTakingSnapshots) {
          return snapshotAtPause;
        }
        updateSelectionBookmarksInElement(element);
        return getCleanSnapshot();
      },
      measureSnapshot: html => html.length,
      restoreSnapshot: html => {
        const hasFocus = element.contains(this.document.activeElement);

        morphdom(element, `<div>${html}</div>`, {
          childrenOnly: true,
        });

        if (hasFocus) {
          deserializeSelectionBookmarksInElement(element);
        }

        // Set a flag and delay snapshotting.
        didRestoreSnapshot = true;
        snapshotTrigger$.next();
      },
    });

    const snapshotTriggerService = createService(...snapshotTriggerServices);
    snapshotTriggerService.changes$.subscribe(snapshotTrigger$);

    let didCommitInitialSnapshot = false;
    const snapshotService = createService({
      effects$: snapshotTrigger$.pipe(
        debounceTime(0),
        tap(() => {
          if (didRestoreSnapshot) {
            didRestoreSnapshot = false;
            return;
          }

          undoManager.takeSnapshot();

          if (!didCommitInitialSnapshot) {
            undoManager.commitLastSnapshot();
            didCommitInitialSnapshot = true;
          }
        }),
        share(),
      ),
    });

    const keyboardService = createService({
      effects$: fromEvent<KeyboardEvent>(element, 'keydown').pipe(
        tap(event => {
          // Note: we've seen cases of event.key being undefined.
          const key = event.key ? event.key.toLowerCase() : '';

          if (eventHasMetaKeyForPlatform(event) && key === 'z') {
            event.preventDefault();

            if (event.shiftKey) {
              undoManager.redo();
            } else {
              undoManager.undo();
            }
          }

          if (eventHasMetaKeyForPlatform(event) && key === 'y') {
            event.preventDefault();

            undoManager.redo();
          }
        }),
        share(),
      ),
    });

    super(element, snapshotService, keyboardService);

    this.undoManager = undoManager;
    this.pauseTakingSnapshots = () => {
      if (pauseTakingSnapshots) {
        return noop;
      }
      snapshotAtPause = element.innerHTML;
      pauseTakingSnapshots = true;
      return () => {
        pauseTakingSnapshots = false;
      };
    };
  }

  private readonly undoManager: UndoManager<string>;

  commitLastSnapshot() {
    this.undoManager.commitLastSnapshot();
  }

  takeAndCommitSnapshot() {
    this.undoManager.takeSnapshot();
    this.undoManager.commitLastSnapshot();
  }

  pauseTakingSnapshots: () => () => void;
}
